import type { AcaoDePainel } from "@/api/contrato";

/*
 * Os rótulos da trilha de auditoria.
 *
 * `Record` fecha o catálogo no compilador, e `Auditoria.test.tsx` o confere contra o contrato
 * gerado: ação nova sem rótulo reprova aqui, e não aparece crua no filtro. A LINHA é outra
 * história: pode ser de dois anos atrás, e por isso `acao` é texto, e `rotuloDaAcao` devolve
 * cru o que o catálogo de hoje não conhece.
 */
export const ROTULOS_DAS_ACOES: Record<AcaoDePainel, string> = {
  login: "Entrar",
  logout: "Sair",
  resolver_divergencia: "Resolver divergência",
  revogar_chave_api: "Revogar chave",
  bloquear_convenio: "Bloquear convênio",
  desbloquear_convenio: "Desbloquear convênio",
  consultar_boleto_completo: "Consultar boleto completo",
  cancelar_boleto: "Cancelar boleto",
  reprocessar_boleto: "Reprocessar boleto",
  resolver_indeterminado: "Resolver indeterminado",
  reprocessar_fila_morta: "Reprocessar fila morta",
  testar_entrega: "Testar entrega",
  emitir_chave_api: "Emitir chave",
  criar_pessoa: "Criar cedente",
  criar_conta_bancaria: "Criar conta",
  criar_sistema: "Criar aplicação",
  configurar_entrega: "Configurar entrega",
  criar_convenio: "Criar convênio",
  criar_vinculo: "Criar vínculo",
  criar_plano: "Criar plano",
  criar_contrato: "Criar contrato",
  definir_convenios_do_vinculo: "Definir convênios do vínculo",
  alterar_plano: "Alterar plano",
  encerrar_contrato: "Encerrar contrato",
  trocar_plano: "Trocar plano",
  alterar_cobranca_do_sistema: "Alterar cobrança da aplicação",
  suspender_vinculo: "Suspender vínculo",
  reativar_vinculo: "Reativar vínculo",
  definir_limite_do_vinculo: "Definir limite do vínculo",
  fechar_competencia: "Fechar competência",
  fechar_fatura: "Fechar fatura",
  registrar_baixa_de_fatura: "Registrar baixa de fatura",
  cancelar_fatura: "Cancelar fatura",
  adicionar_rede_permitida: "Adicionar rede permitida",
  remover_rede_permitida: "Remover rede permitida",
  alterar_pessoa: "Alterar cedente",
  alterar_conta_bancaria: "Alterar conta",
  alterar_convenio: "Alterar convênio",
  pedir_verificacao_de_boleto: "Verificar boleto",
  pedir_sonda_de_boleto: "Procurar título",
  tratar_titulo_orfao: "Tratar título órfão",
  ver_pdf_de_boleto: "Ver PDF",
  exportar_boletos: "Exportar boletos",
  reenviar_entrega: "Reenviar entrega",
  suspender_sistema: "Suspender aplicação",
  reativar_sistema: "Reativar aplicação",
  corrigir_nome_do_sistema: "Corrigir nome da aplicação",
  enviar_certificado: "Enviar certificado",
  conferir_certificado: "Conferir certificado",
  provisionar_convenio: "Provisionar convênio",
};

/** A entidade não tem enum no contrato: a rota aceita qualquer texto, e o que não está aqui sai cru. */
const ROTULOS_DAS_ENTIDADES: Record<string, string> = {
  api_key: "Chave de acesso",
  boleto: "Boleto",
  conciliacao_divergencia: "Divergência",
  conta_bancaria: "Conta",
  contrato: "Contrato",
  convenio: "Convênio",
  entrega: "Entrega",
  fatura: "Fatura",
  fechamento_competencia: "Fechamento",
  fila: "Fila morta",
  pessoa: "Cedente",
  plano: "Plano",
  sessao: "Sessão",
  sistema: "Aplicação",
  sistema_rede_permitida: "Rede permitida",
  titulo_orfao: "Título órfão",
  vinculo_sistema_pessoa: "Vínculo",
};

/*
 * As palavras das chaves de `antes` e `depois`. A chave é coluna, sem acento: "SITUACAO" na
 * tela é banco de dados vazando. Dinheiro já sai como R$ em `ValorDoEstado`, e por isso o
 * sufixo `_centavos` some do rótulo.
 */
const ACENTOS: Readonly<Record<string, string>> = {
  agencia: "agência",
  cobranca: "cobrança",
  codigo: "código",
  competencia: "competência",
  convenio: "convênio",
  convenios: "convênios",
  descricao: "descrição",
  disponivel: "disponível",
  inicio: "início",
  instantaneo: "instantâneo",
  numero: "número",
  observacao: "observação",
  operacao: "operação",
  padrao: "padrão",
  politica: "política",
  preco: "preço",
  proxima: "próxima",
  proximo: "próximo",
  razao: "razão",
  situacao: "situação",
  unitario: "unitário",
  verificacao: "verificação",
};

export function rotuloDoCampo(chave: string): string {
  return chave
    .replace(/_centavos$/, "")
    .split("_")
    .map((palavra) => ACENTOS[palavra] ?? palavra)
    .join(" ");
}

function emOrdem(rotulos: Record<string, string>): readonly (readonly [string, string])[] {
  return Object.entries(rotulos).sort(([, a], [, b]) => a.localeCompare(b, "pt-BR"));
}

/** As opções dos filtros, por ordem do rótulo: é o rótulo que o operador procura. */
export const OPCOES_DE_ACAO = emOrdem(ROTULOS_DAS_ACOES);
export const OPCOES_DE_ENTIDADE = emOrdem(ROTULOS_DAS_ENTIDADES);

/** `hasOwn`: a linha é texto do banco, e `constructor` não pode virar rótulo. */
function rotuloDe(rotulos: Record<string, string>, valor: string): string {
  return Object.hasOwn(rotulos, valor) ? (rotulos[valor] ?? valor) : valor;
}

export function rotuloDaAcao(acao: string): string {
  return rotuloDe(ROTULOS_DAS_ACOES, acao);
}

export function rotuloDaEntidade(entidade: string): string {
  return rotuloDe(ROTULOS_DAS_ENTIDADES, entidade);
}
